import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setChildLeads,
  setFilters,
  setLeads,
  setMode,
  setParentLead,
  setSelectedLead,
} from "../../../state/reducers/leadSlice";
import axios from "axios";
import { toggleLeadFormOpen } from "../../../state/reducers/applicationSlice";
import { formatDateTime } from "../../../utils/dateFormat";
import { addLeadNotes, getLeadNotes } from "../../../services/lead.services";
import Modal from "react-modal";
import { formatDistanceToNow } from "date-fns";
import {
  IconBell,
  IconArrowUp,
  IconArrowDown,
  IconEye,
  IconEdit,
  IconTrash,
  IconSearch,
  IconFilter,
  IconX,
  IconPlus,
  IconCheck,
  IconDownload,
  IconRefresh,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { FaCheck } from "react-icons/fa";
import { IconUsers, IconUser } from "@tabler/icons-react";
import toast from "react-hot-toast";
import { Baby } from "lucide-react";
import * as XLSX from "xlsx";

// Multi-Select Dropdown Component
const MultiSelectDropdown = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options...",
  disabled = false,
  searchPlaceholder = "Search options...",
  showSearch = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter options based on search term (only if showSearch is true)
  const filteredOptions = showSearch
    ? options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.value.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        if (showSearch) {
          setSearchTerm(""); // Clear search when closing (only if showSearch is true)
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSearch]);

  // Focus search input when dropdown opens (only if showSearch is true)
  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, showSearch]);

  const handleDropdownToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen && showSearch) {
        setSearchTerm(""); // Clear search when opening (only if showSearch is true)
      }
    }
  };

  const handleOptionToggle = (optionValue) => {
    const newSelectedValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((value) => value !== optionValue)
      : [...selectedValues, optionValue];

    onChange(newSelectedValues);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === filteredOptions.length) {
      // If all filtered options are selected, deselect them
      const filteredValues = filteredOptions.map((option) => option.value);
      const newSelectedValues = selectedValues.filter(
        (value) => !filteredValues.includes(value)
      );
      onChange(newSelectedValues);
    } else {
      // Select all filtered options
      const filteredValues = filteredOptions.map((option) => option.value);
      const newSelectedValues = [
        ...new Set([...selectedValues, ...filteredValues]),
      ];
      onChange(newSelectedValues);
    }
  };

  const handleClearAll = () => {
    if (showSearch && searchTerm) {
      // If there's a search term, clear only the filtered options
      const filteredValues = filteredOptions.map((option) => option.value);
      const newSelectedValues = selectedValues.filter(
        (value) => !filteredValues.includes(value)
      );
      onChange(newSelectedValues);
    } else {
      // If no search term, clear all selected options
      onChange([]);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const option = options.find((opt) => opt.value === selectedValues[0]);
      return option ? option.label : selectedValues[0];
    }
    return `${selectedValues.length} selected`;
  };

  // Check if all filtered options are selected
  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => selectedValues.includes(option.value));

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={handleDropdownToggle}
        disabled={disabled}
        className={`w-full border border-gray-300 rounded-md p-2 text-sm text-left focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between ${
          disabled
            ? "bg-gray-100 cursor-not-allowed"
            : "bg-white hover:bg-gray-50"
        }`}
      >
        <div className="flex items-center flex-1 min-w-0">
          <span
            className={`truncate ${
              selectedValues.length === 0 ? "text-gray-500" : "text-gray-700"
            }`}
          >
            {getDisplayText()}
          </span>
          {selectedValues.length > 0 && (
            <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0">
              {selectedValues.length}
            </span>
          )}
        </div>
        {isOpen ? (
          <IconChevronUp size={16} className="text-gray-400" />
        ) : (
          <IconChevronDown size={16} className="text-gray-400" />
        )}
      </button>

      {isOpen && (
        <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-80 overflow-hidden">
          {/* Search Input - Only show if showSearch is true */}
          {showSearch && (
            <div className="sticky top-0 bg-white px-3 py-2 border-b border-gray-200 z-10">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder={searchPlaceholder}
                  className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <IconSearch
                  size={16}
                  className="absolute left-2.5 top-2.5 text-gray-400"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600"
                  >
                    <IconX size={16} />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Header with Select All/Clear All */}
          <div
            className={`sticky ${
              showSearch ? "top-[52px]" : "top-0"
            } bg-gray-50 px-3 py-2 border-b border-gray-200 flex justify-between items-center z-10`}
          >
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              {allFilteredSelected ? "Deselect All" : "Select All"}
              {showSearch && searchTerm && filteredOptions.length > 0 && (
                <span className="ml-1 text-gray-500">
                  ({filteredOptions.length})
                </span>
              )}
            </button>
            {selectedValues.length > 0 && (
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs text-red-600 hover:text-red-800 font-medium"
              >
                {showSearch && searchTerm ? "Clear Filtered" : "Clear All"}
              </button>
            )}
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              <div className="py-1">
                {filteredOptions.map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(option.value)}
                      onChange={() => handleOptionToggle(option.value)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-3"
                    />
                    <span className="text-sm text-gray-700 flex-1">
                      {option.label}
                    </span>
                    {selectedValues.includes(option.value) && (
                      <IconCheck size={14} className="text-blue-600 ml-2" />
                    )}
                  </label>
                ))}
              </div>
            ) : (
              <div className="px-3 py-4 text-center">
                <IconSearch className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">
                  {showSearch && searchTerm
                    ? `No options found for "${searchTerm}"`
                    : "No options available"}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Selected Values Display Component
const SelectedValuesDisplay = ({
  selectedValues,
  options,
  onRemove,
  label,
}) => {
  if (selectedValues.length === 0) return null;

  return (
    <div className="mt-2">
      <div className="text-xs text-gray-600 mb-1">{label}:</div>
      <div className="flex flex-wrap gap-1">
        {selectedValues.map((value) => {
          const option = options.find((opt) => opt.value === value);
          const displayName = option ? option.label : value;

          return (
            <span
              key={value}
              className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full"
            >
              {displayName}
              <button
                type="button"
                onClick={() => onRemove(value)}
                className="ml-1 text-blue-600 hover:text-blue-800"
              >
                <IconX size={12} />
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
};

function LeadTable({ fetchLeads }) {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [leadsPerPage, setLeadsPerPage] = useState(10);
  const userId = useSelector((state) => state.user.user_id);
  const [reminderMessage, setReminderMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [currentLead, setCurrentLead] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [leadToUpdate, setLeadToUpdate] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");
  const [sortedLeads, setSortedLeads] = useState([]);
  const [notesSortDirection, setNotesSortDirection] = useState("desc");
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [showFilters, setShowFilters] = useState(true);
  const admins = useSelector((state) => state.admin.admins);
  const leads = useSelector((state) => state.leads.leads);
  const filteredLeads = useSelector((state) => state.leads.filteredLeads);
  const filters = useSelector((state) => state.leads.filters);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const role = useSelector((state) => state.user.role);
  const [activeLeadId, setActiveLeadId] = useState(null);
  const [isDropMessageChecked, setIsDropMessageChecked] = useState(false);
  // Mobile responsive
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [showMobileActions, setShowMobileActions] = useState(null);

  // Updated filters state to use arrays for multi-select
  const [localFilters, setLocalFilters] = useState({
    assignedFilter: [],
    callHandledByFilter: [],
    statusFilter: [],
    typeFilter: [],
    fromDateFilter: "",
    toDateFilter: "",
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);
  console.log("admin", admins);

  // Prepare options for multi-select dropdowns
  const filterOptions = {
    assigned: admins.map((admin) => ({
      value: admin.admin_id.toString(),
      label: admin.username,
    })),

    callHandledBy: admins
      .filter((admin) => admin.role === "admin")
      .map((admin) => ({
        value: admin.admin_id.toString(),
        label: admin.username,
      })),

    status: [
      { value: "fresh", label: "Fresh" },
      { value: "enquiry", label: "Enquiry" },
      { value: "customer", label: "Customer" },
      { value: "junk", label: "Junk" },
      { value: "lost", label: "Lost" },
    ],

    leadType: [
      { value: "abroad_counseling", label: "Abroad Counseling" },
      { value: "career_counseling", label: "Career Counseling" },
      { value: "subject_selection", label: "Subject Selection" },
      { value: "test_prep", label: "Test Prep" },
      { value: "visa", label: "Visa" },
      { value: "profile_building", label: "Profile Building" },
      { value: "others", label: "Others" },
    ],
  };

  const exportToXLSX = (data, admins, filename = "leads_export.xlsx") => {
    if (!data || !data.length) return;

    const exportData = data.map((lead) => {
      const parent = lead.parent_info || {};
      const assignedAdmin = admins.find((a) => a.admin_id === lead.assign_by);
      const latestNote = lead.notes?.[0]?.note || "";

      return {
        Name: parent.name || "",
        Phone: parent.phone || "",
        Status: lead.status || "",
        "Lead Type": lead.lead_type || "",
        "Contacted At": lead.contacted_at || "",
        "Created At": lead.created_at || "",
        Branch: lead.branch || "",
        "Assigned To": assignedAdmin?.username || "N/A",
        Email: parent.email || "",
        Address: parent.address || "",
        Notes: latestNote,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

    XLSX.writeFile(workbook, filename);
  };
  console.log("leads", leads);

  // Updated sort and filter logic for multi-select
  useEffect(() => {
    if (leads && leads.length > 0) {
      // Filter by search term
      let result = leads.filter((lead) => {
        const name = lead.parent_info?.name?.toLowerCase() || "";
        const phone = lead.parent_info?.phone?.toLowerCase() || "";
        const term = debouncedSearchTerm.toLowerCase();
        return name.includes(term) || phone.includes(term);
      });

      // Apply assigned filter (multi-select)
      if (localFilters.assignedFilter.length > 0) {
        result = result.filter(
          (lead) =>
            lead.assign_by &&
            localFilters.assignedFilter.includes(lead.assign_by.toString())
        );
      }

      // Apply call handled by filter (multi-select)
      if (localFilters.callHandledByFilter.length > 0) {
        result = result.filter((lead) =>
          localFilters.callHandledByFilter.includes(
            lead.call_handled_by?.toString()
          )
        );
      }

      // Apply status filter (multi-select)
      if (localFilters.statusFilter.length > 0) {
        result = result.filter((lead) =>
          localFilters.statusFilter.includes(lead.status)
        );
      } else {
        // Exclude junk leads by default if no status filter is applied
        result = result.filter((lead) => lead.status !== "junk");
      }

      // Apply lead type filter (multi-select)
      if (localFilters.typeFilter.length > 0) {
        result = result.filter((lead) =>
          localFilters.typeFilter.includes(lead.lead_type)
        );
      }

      // Apply date range filters
      if (localFilters.fromDateFilter && localFilters.toDateFilter) {
        const fromDate = new Date(localFilters.fromDateFilter);
        const toDate = new Date(localFilters.toDateFilter);
        toDate.setDate(toDate.getDate() + 1);

        result = result.filter((lead) => {
          const leadDate = new Date(lead.created_at);
          return leadDate >= fromDate && leadDate <= toDate;
        });
      }

      // Get latest note date for sorting
      const getLatestNoteDate = (lead) => {
        if (!lead.notes || lead.notes.length === 0) return null;
        const latestNote = lead.notes
          .filter((note) => note.created_at)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        return latestNote ? new Date(latestNote.created_at) : null;
      };

      // Sort by appropriate field
      if (sortBy === "created_at") {
        result.sort((a, b) => {
          const dateA = new Date(a.created_at);
          const dateB = new Date(b.created_at);
          return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
        });
      } else if (sortBy === "notes") {
        result.sort((a, b) => {
          const noteDateA = getLatestNoteDate(a);
          const noteDateB = getLatestNoteDate(b);

          if (!noteDateA && !noteDateB) return 0;
          if (!noteDateA) return 1;
          if (!noteDateB) return -1;

          return notesSortDirection === "asc"
            ? noteDateA - noteDateB
            : noteDateB - noteDateA;
        });
      }

      setSortedLeads(result);
    } else {
      setSortedLeads([]);
    }
  }, [
    leads,
    localFilters,
    sortDirection,
    notesSortDirection,
    debouncedSearchTerm,
    sortBy,
  ]);

  // Reset page when filters or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [localFilters, sortDirection, sortBy]);

  // Toggle sort direction
  const toggleSortDirection = () => {
    setSortBy("created_at");
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  // Toggle notes sort direction
  const toggleNotesSortDirection = () => {
    setSortBy("notes");
    setNotesSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  // Handle multi-select filter change
  const handleFilterChange = (filterName, selectedValues) => {
    setLocalFilters((prev) => ({
      ...prev,
      [filterName]: selectedValues,
    }));
  };

  // Handle removing single filter value
  const handleRemoveFilterValue = (filterName, valueToRemove) => {
    const newValues = localFilters[filterName].filter(
      (value) => value !== valueToRemove
    );
    handleFilterChange(filterName, newValues);
  };

  // Handle date filter change
  const handleDateFilterChange = (filterName, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  // Reset all filters
  const resetFilters = () => {
    setLocalFilters({
      assignedFilter: [],
      callHandledByFilter: [],
      statusFilter: [],
      typeFilter: [],
      fromDateFilter: "",
      toDateFilter: "",
    });
    setSearchTerm("");
  };

  // Count active filters
  const activeFiltersCount = Object.values(localFilters).reduce(
    (count, filterValue) => {
      if (Array.isArray(filterValue)) {
        return count + filterValue.length;
      }
      return count + (filterValue !== "" ? 1 : 0);
    },
    0
  );

  // Fetch leads on component mount
  useEffect(() => {
    fetchLeads();
  }, []);

  // Handle creating child lead
  const handleCreateChildLead = (lead) => {
    dispatch(setSelectedLead(""));
    dispatch(setChildLeads([]));
    dispatch(setParentLead(lead));
    dispatch(setMode("create-child"));
    dispatch(toggleLeadFormOpen());
  };

  // Handle bell click for reminders
  const handleBellClick = (message) => {
    setReminderMessage(message);
    setModelOpen(true);
  };

  // Handle view lead form
  const handleViewLeadForm = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/lead/api/${id}/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const selectedlead = response.data.data.parent_lead || {};
      const parent_info = response.data.data.parent_info || {};
      const childLeads = response.data.data.child_leads || [];

      dispatch(setSelectedLead({ ...parent_info, ...selectedlead }));
      dispatch(setParentLead(parent_info));
      dispatch(setChildLeads(childLeads));
      dispatch(toggleLeadFormOpen());
      dispatch(setMode("view"));
    } catch (err) {
      console.error("Error fetching leads:", err);
      toast.error("Failed to view lead details");
    }
  };

  // Handle edit lead
  const handleEditLead = async (id) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/lead/api/${id}/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const selectedlead = response.data.data.parent_lead || {};
      const parent_info = response.data.data.parent_info || {};
      const childLeads = response.data.data.child_leads || [];

      dispatch(setSelectedLead({ ...parent_info, ...selectedlead }));
      dispatch(setParentLead(parent_info));
      dispatch(setChildLeads(childLeads));
      dispatch(toggleLeadFormOpen());
      dispatch(setMode("edit"));
    } catch (err) {
      console.error("Error fetching leads:", err);
      toast.error("Failed to edit lead");
    }
  };

  // Handle delete lead
  const handleDeleteLead = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this lead?"
    );
    if (!isConfirmed) return;

    try {
      await axios.delete(`${process.env.REACT_APP_BASE_URL}/lead/api/${id}/`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      toast.success("Lead deleted successfully.");
      fetchLeads();
    } catch (err) {
      console.error("Error deleting lead:", err);
      toast.error("Failed to delete lead.");
    }
  };

  // Handle clicking on notes
  const handleNoteClick = async (lead) => {
    setCurrentLead(lead);
    setNewNote("");
    setIsModalOpen(true);

    try {
      const res = await getLeadNotes(lead);
      setNotes(res.data);
    } catch (err) {
      console.error("Failed to fetch notes:", err);
      setNotes([]);
      toast.error("Failed to load notes");
    }
  };

  // Handle adding a new note
  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Note cannot be empty");
      return;
    }

    try {
      const noteData = { lead: currentLead.id, note: newNote };
      await addLeadNotes(currentLead, noteData);

      // Optimistic update
      setNotes((prevNotes) => [
        {
          note: newNote,
          created_at: new Date().toISOString(),
          user_name:
            admins.find((admin) => admin.admin_id === userId)?.username ||
            "You",
        },
        ...prevNotes,
      ]);

      setNewNote("");

      // Fetch updated notes
      const res = await getLeadNotes(currentLead);
      setNotes(res.data);

      fetchLeads();
      toast.success("Note added successfully");
    } catch (err) {
      console.error("Failed to add note:", err);
      toast.error("Failed to add note");
    }
  };

  // Handle checkbox selection
  const handleCheckboxChange = (leadId) => {
    setSelectedLeads((prevSelected) =>
      prevSelected.includes(leadId)
        ? prevSelected.filter((id) => id !== leadId)
        : [...prevSelected, leadId]
    );
  };

  // Handle status update for selected leads
  const handleStatusUpdate = async (status) => {
    if (selectedLeads.length === 0) {
      toast.info("Please select at least one lead to update.");
      return;
    }

    if (status === "lost") {
      setIsReasonModalOpen(true);
      return;
    }

    try {
      const promises = selectedLeads.map((leadId) =>
        axios.patch(
          `${process.env.REACT_APP_BASE_URL}/lead/api/${leadId}/`,
          { status },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        )
      );

      const res = await Promise.all(promises);
      toast.success(`Leads updated to ${status} successfully.`);
      setSelectedLeads([]);
      fetchLeads();
    } catch (err) {
      console.error("Failed to update lead status:", err);
      toast.error("An error occurred while updating lead status.");
    }
  };

  // Handle submit reason for lost lead
  const handleSubmitReason = async () => {
    if (!reason.trim()) {
      toast.info("Please enter a reason for marking this lead as lost.");
      return;
    }

    if (!leadToUpdate) {
      toast.error("No lead selected.");
      return;
    }

    const fullLead = leads.find((lead) => lead.id === leadToUpdate);
    if (!fullLead) {
      toast.error("Error: Selected lead not found.");
      return;
    }

    try {
      await addLeadNotes(fullLead, { note: `Reason for lost: ${reason}` });

      await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/lead/api/${leadToUpdate}/`,
        { status: "lost" },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success("Lead marked as lost with reason.");
      setReason("");
      setLeadToUpdate(null);
      setIsReasonModalOpen(false);
      setSelectedLeads([]);
      fetchLeads();
    } catch (err) {
      console.error("Failed to update lead status:", err);
      toast.error("An error occurred while updating lead status.");
    }
  };

  // Handle status change
  const handleChangeStatus = async (id, newStatus) => {
    try {
      let endpoint = `${process.env.REACT_APP_BASE_URL}/lead/api/${id}/`;
      if (newStatus === "enquiry") {
        endpoint = `${process.env.REACT_APP_BASE_URL}/lead/api/${id}/update-status-enquriy/`;
      } else if (newStatus === "fresh") {
        endpoint = `${process.env.REACT_APP_BASE_URL}/lead/api/${id}/update-status-fresh/`;
      }

      await axios.patch(
        endpoint,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(`Status updated to ${newStatus}`);
      fetchLeads();
    } catch (err) {
      console.error("Error updating status:", err.message);
      toast.error("Failed to update status. Please try again.");
    }
  };

  // Pagination logic
  const totalLeads = sortedLeads.length;
  const totalPages = Math.ceil(totalLeads / leadsPerPage) || 1;
  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;
  const currentLeads = useMemo(() => {
    return sortedLeads.slice(indexOfFirstLead, indexOfLastLead);
  }, [sortedLeads, currentPage, leadsPerPage]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo(0, 0);
    }
  };

  // Handle items per page change
  const handleLeadsPerPageChange = (newValue) => {
    setLeadsPerPage(Number(newValue));
    setCurrentPage(1);
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "customer":
        return "bg-green-100 text-green-700 border border-green-300";
      case "enquiry":
        return "bg-orange-100 text-orange-700 border border-orange-300";
      case "lost":
        return "bg-yellow-100 text-yellow-700 border border-yellow-300";
      case "junk":
        return "bg-red-100 text-red-700 border border-red-300";
      default:
        return "bg-blue-100 text-blue-700 border border-blue-300";
    }
  };

  // Toggle row expansion (for mobile)
  const toggleRowExpansion = (leadId) => {
    setActiveLeadId(activeLeadId === leadId ? null : leadId);
  };

  const handleDropMessageToggle = () => {
    const newCheckedState = !isDropMessageChecked;
    setIsDropMessageChecked(newCheckedState);
    if (newCheckedState) {
      setNewNote("Call not received, drop a message.");
    } else {
      setNewNote("");
    }
  };

  function capitalizeName(name) {
    return name
      ?.split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Filters Section */}
      <div className="mb-5 bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center">
            <IconFilter className="text-gray-500 mr-2" size={20} />
            <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
            {activeFiltersCount > 0 && (
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-gray-500 hover:text-gray-700 p-1 rounded transition-colors"
            >
              {showFilters ? (
                <IconChevronUp size={20} className="text-gray-500" />
              ) : (
                <IconChevronDown size={20} className="text-gray-500" />
              )}
            </button>
            <button
              onClick={resetFilters}
              className="text-blue-600 hover:text-blue-800 text-sm flex items-center transition-colors"
            >
              <IconRefresh size={16} className="mr-1" />
              Reset
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 relative" style={{ minHeight: "320px" }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {/* Assigned To Filter */}
              <div className="space-y-2 relative" style={{ zIndex: 45 }}>
                <label className="block text-sm font-medium text-gray-700">
                  Assigned To
                </label>
                <MultiSelectDropdown
                  options={filterOptions.assigned}
                  selectedValues={localFilters.assignedFilter}
                  onChange={(values) =>
                    handleFilterChange("assignedFilter", values)
                  }
                  placeholder="All Assigned"
                />
                <SelectedValuesDisplay
                  selectedValues={localFilters.assignedFilter}
                  options={filterOptions.assigned}
                  onRemove={(value) =>
                    handleRemoveFilterValue("assignedFilter", value)
                  }
                  label="Selected Assigned"
                />
              </div>

              {/* Call Handled By Filter */}
              <div className="space-y-2 relative" style={{ zIndex: 44 }}>
                <label className="block text-sm font-medium text-gray-700">
                  Call Handled By
                </label>
                <MultiSelectDropdown
                  options={filterOptions.callHandledBy}
                  selectedValues={localFilters.callHandledByFilter}
                  onChange={(values) =>
                    handleFilterChange("callHandledByFilter", values)
                  }
                  placeholder="All Call Handlers"
                />
                <SelectedValuesDisplay
                  selectedValues={localFilters.callHandledByFilter}
                  options={filterOptions.callHandledBy}
                  onRemove={(value) =>
                    handleRemoveFilterValue("callHandledByFilter", value)
                  }
                  label="Selected Call Handlers"
                />
              </div>

              {/* Status Filter */}
              <div className="space-y-2 relative" style={{ zIndex: 43 }}>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <MultiSelectDropdown
                  options={filterOptions.status}
                  selectedValues={localFilters.statusFilter}
                  onChange={(values) =>
                    handleFilterChange("statusFilter", values)
                  }
                  showSearch={false}
                  placeholder="All Status"
                />
                <SelectedValuesDisplay
                  selectedValues={localFilters.statusFilter}
                  options={filterOptions.status}
                  onRemove={(value) =>
                    handleRemoveFilterValue("statusFilter", value)
                  }
                  label="Selected Status"
                />
              </div>

              {/* Lead Type Filter */}
              <div className="space-y-2 relative" style={{ zIndex: 42 }}>
                <label className="block text-sm font-medium text-gray-700">
                  Lead Type
                </label>
                <MultiSelectDropdown
                  options={filterOptions.leadType}
                  selectedValues={localFilters.typeFilter}
                  onChange={(values) =>
                    handleFilterChange("typeFilter", values)
                  }
                  placeholder="All Lead Types"
                  showSearch={false}
                />
                <SelectedValuesDisplay
                  selectedValues={localFilters.typeFilter}
                  options={filterOptions.leadType}
                  onRemove={(value) =>
                    handleRemoveFilterValue("typeFilter", value)
                  }
                  label="Selected Lead Types"
                />
              </div>

              {/* From Date Filter */}
              <div className="space-y-2 relative" style={{ zIndex: 10 }}>
                <label className="block text-sm font-medium text-gray-700">
                  From Date
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md p-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  value={localFilters.fromDateFilter}
                  onChange={(e) =>
                    handleDateFilterChange("fromDateFilter", e.target.value)
                  }
                />
              </div>

              {/* To Date Filter */}
              <div className="space-y-2 relative" style={{ zIndex: 10 }}>
                <label className="block text-sm font-medium text-gray-700">
                  To Date
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md p-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  value={localFilters.toDateFilter}
                  onChange={(e) =>
                    handleDateFilterChange("toDateFilter", e.target.value)
                  }
                />
              </div>

              {/* Search Filter */}
              <div
                className="space-y-2 relative col-span-1 sm:col-span-2 lg:col-span-1"
                style={{ zIndex: 10 }}
              >
                <label className="block text-sm font-medium text-gray-700">
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name or phone..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <IconSearch
                    className="absolute left-3 top-2.5 text-gray-400"
                    size={18}
                  />
                </div>
              </div>
            </div>

            {/* Summary of Active Filters */}
            {activeFiltersCount > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <strong>Active Filters Summary:</strong>
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  {Object.entries(localFilters).map(([key, values]) => {
                    if (Array.isArray(values) && values.length > 0) {
                      return (
                        <span key={key} className="mr-4">
                          {key.replace("Filter", "").charAt(0).toUpperCase() +
                            key.replace("Filter", "").slice(1)}
                          : {values.length} selected
                        </span>
                      );
                    }
                    if (typeof values === "string" && values !== "") {
                      return (
                        <span key={key} className="mr-4">
                          {key.replace("Filter", "").charAt(0).toUpperCase() +
                            key.replace("Filter", "").slice(1)}
                          : {values}
                        </span>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Leads Table Section */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-gray-200">
          <div className="flex items-center mb-3 sm:mb-0">
            <h2 className="text-xl font-bold text-gray-800 mr-3">Leads</h2>
            <span className="text-sm text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded">
              {totalLeads} {totalLeads === 1 ? "lead" : "leads"}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => exportToXLSX(sortedLeads, admins)}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center"
            >
              <IconDownload size={16} className="mr-1" />
              Export Leads
            </button>

            <button
              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              onClick={() => handleStatusUpdate("junk")}
              disabled={selectedLeads.length === 0}
            >
              <IconTrash size={16} className="mr-1" />
              Mark as Junk
            </button>

            <button
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              onClick={() => {
                if (selectedLeads.length === 1) {
                  setLeadToUpdate(selectedLeads[0]);
                  setIsReasonModalOpen(true);
                } else {
                  toast.info(
                    "Please select only one lead at a time for marking as lost."
                  );
                }
              }}
              disabled={selectedLeads.length !== 1}
            >
              <IconX size={16} className="mr-1" />
              Mark as Lost
            </button>
          </div>
        </div>

        {/* Mobile View */}
        {isMobileView ? (
          <div className="divide-y divide-gray-200">
            {currentLeads.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <IconSearch className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                <p className="text-lg font-medium text-gray-700 mb-1">
                  No leads found
                </p>
                <p className="text-sm">
                  Try adjusting your filters or search criteria
                </p>
              </div>
            ) : (
              currentLeads.map((lead) => {
                const sortedNotes = [...(lead.notes || [])].sort(
                  (a, b) => new Date(b.created_at) - new Date(a.created_at)
                );
                const latestNote = sortedNotes[0];

                return (
                  <div key={lead.id} className="p-4">
                    <div
                      className="flex items-center justify-between"
                      onClick={() => toggleRowExpansion(lead.id)}
                    >
                      <div className="flex items-start">
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleCheckboxChange(lead.id);
                          }}
                          className="h-4 w-4 text-blue-600 mr-3 mt-1"
                        />
                        <div>
                          <div className="flex items-center">
                            {lead.child_id ? (
                              <Baby className="text-pink-500 mr-1" size={16} />
                            ) : (
                              <IconUsers
                                className="text-green-600 mr-1"
                                size={16}
                              />
                            )}
                            <span className="font-medium text-gray-900">
                              {lead.parent_info?.name}
                            </span>

                            {lead.reminder_message && (
                              <IconBell
                                className="ml-2 text-amber-500"
                                size={16}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBellClick(lead.reminder_message);
                                }}
                              />
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            Assigned:{" "}
                            {capitalizeName(
                              admins.find(
                                (admin) => admin.admin_id === lead.assign_by
                              )?.username
                            ) || "N/A"}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                            lead.status
                          )}`}
                        >
                          {lead.status}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          {formatDateTime(lead.created_at).split(" ")[0]}
                        </span>
                      </div>
                    </div>

                    {activeLeadId === lead.id && (
                      <div className="mt-3 pl-7">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm mb-3">
                          <div>
                            <span className="text-gray-500">Type:</span>
                            <span className="ml-1 font-medium">
                              {lead.lead_type}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Branch:</span>
                            <span className="ml-1 font-medium">
                              {lead.branch || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div
                          className={`p-3 rounded-md mb-3 border ${
                            latestNote
                              ? "bg-gray-50 cursor-pointer hover:bg-gray-100"
                              : "bg-blue-50 cursor-pointer hover:bg-blue-100"
                          }`}
                          onClick={() => handleNoteClick(lead)}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-medium text-gray-700">
                              {latestNote ? "Latest Note:" : "No notes yet"}
                            </div>
                            {latestNote && (
                              <div className="text-xs text-gray-500">
                                {formatDistanceToNow(
                                  new Date(latestNote.created_at),
                                  {
                                    addSuffix: true,
                                  }
                                )}
                              </div>
                            )}
                          </div>

                          <div className="text-sm text-gray-600 max-h-[100px] overflow-y-auto pr-1 whitespace-pre-line">
                            {latestNote ? latestNote.note : "Tap to add a note"}
                          </div>

                          {latestNote && (
                            <div className="text-xs text-gray-500 mt-1">
                              By: {latestNote.user_name}
                            </div>
                          )}
                        </div>

                        <div className="flex space-x-2 mt-3">
                          <button
                            className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded-md inline-flex items-center justify-center cursor-pointer transform transition-all duration-200 hover:scale-105 shadow-sm"
                            onClick={() => handleViewLeadForm(lead.id)}
                          >
                            <IconEye size={16} className="text-white" />
                          </button>

                          {!lead.child_id && (
                            <button
                              className="p-1.5 bg-purple-500 hover:bg-purple-600 rounded-md inline-flex items-center justify-center cursor-pointer transform transition-all duration-200 hover:scale-105 shadow-sm"
                              onClick={() => handleCreateChildLead(lead)}
                            >
                              <Baby size={16} className="text-white" />
                            </button>
                          )}

                          <button
                            className="p-1.5 bg-emerald-500 hover:bg-emerald-600 rounded-md inline-flex items-center justify-center cursor-pointer transform transition-all duration-200 hover:scale-105 shadow-sm"
                            onClick={() => handleEditLead(lead.id)}
                          >
                            <IconEdit size={16} className="text-white" />
                          </button>

                          {role === "superadmin" && (
                            <button
                              className="p-1.5 bg-rose-600 hover:bg-rose-700 rounded-md inline-flex items-center justify-center cursor-pointer transform transition-all duration-200 hover:scale-105 shadow-sm"
                              onClick={() => handleDeleteLead(lead.id)}
                            >
                              <IconTrash size={16} className="text-white" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ) : (
          /* Desktop Table View */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left">
                    <input
                      type="checkbox"
                      onChange={(e) =>
                        setSelectedLeads(
                          e.target.checked
                            ? sortedLeads.map((lead) => lead.id)
                            : []
                        )
                      }
                      checked={
                        sortedLeads.length > 0 &&
                        selectedLeads.length === sortedLeads.length
                      }
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div
                      className="flex items-center cursor-pointer"
                      onClick={toggleSortDirection}
                    >
                      <span>Created At</span>
                      <button className="ml-1 text-gray-400 hover:text-gray-700">
                        {sortDirection === "asc" ? (
                          <IconArrowUp size={14} />
                        ) : (
                          <IconArrowDown size={14} />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div
                      className="flex items-center cursor-pointer"
                      onClick={toggleNotesSortDirection}
                    >
                      <span>Notes</span>
                      <button className="ml-1 text-gray-400 hover:text-gray-700">
                        {notesSortDirection === "asc" ? (
                          <IconArrowUp size={14} />
                        ) : (
                          <IconArrowDown size={14} />
                        )}
                      </button>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentLeads.length > 0 ? (
                  currentLeads.map((lead, index) => (
                    <tr
                      key={lead.id}
                      className="hover:bg-gray-50 transition-colors group"
                    >
                      <td className="px-3 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => handleCheckboxChange(lead.id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {indexOfFirstLead + index + 1}
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-9 w-9 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                            {lead.child_id ? (
                              <Baby className="text-pink-600" size={18} />
                            ) : (
                              <IconUsers className="text-blue-600" size={18} />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center">
                              <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                                {lead.parent_info?.name}
                              </div>
                              {lead.reminder_message && (
                                <IconBell
                                  className="ml-2 text-amber-500 cursor-pointer"
                                  size={16}
                                  onClick={() =>
                                    handleBellClick(lead.reminder_message)
                                  }
                                  title="Reminder"
                                />
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              Assigned to:{" "}
                              <span className="font-medium">
                                {capitalizeName(
                                  admins.find(
                                    (admin) => admin.admin_id === lead.assign_by
                                  )?.username
                                ) || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        {lead.status === "customer" ? (
                          <span className="px-2 py-1 inline-flex text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
                            Customer
                          </span>
                        ) : lead.status === "enquiry" ? (
                          <span className="px-2 py-1 inline-flex text-xs font-medium rounded-full bg-orange-100 text-orange-800 border border-orange-200">
                            Enquiry
                          </span>
                        ) : lead.status === "lost" ? (
                          <span className="px-2 py-1 inline-flex text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                            Lost
                          </span>
                        ) : lead.status === "junk" ? (
                          <span className="px-2 py-1 inline-flex text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200">
                            Junk
                          </span>
                        ) : (
                          <select
                            className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 border border-blue-200 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-200"
                            value={lead.status}
                            onChange={(e) => {
                              handleChangeStatus(lead.id, e.target.value);
                            }}
                          >
                            <option value="fresh">Fresh</option>
                            <option value="enquiry">Enquiry</option>
                          </select>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 capitalize">
                          {lead.lead_type === "others"
                            ? "Others"
                            : lead.lead_type === "subject_selection"
                            ? "Subject Selection"
                            : lead.lead_type
                            ? lead.lead_type.replace(/_/g, " ")
                            : ""}
                        </div>
                        {lead.lead_type === "others" && lead.session_type && (
                          <div className="text-xs text-purple-600 font-medium">
                            ({lead.session_type})
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 leading-tight">
                          {(() => {
                            const [date, time] = formatDateTime(
                              lead.created_at
                            ).split(", ");
                            return (
                              <>
                                <div>{date}</div>
                                <div className="text-xs text-gray-500">
                                  {time}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </td>

                      <td className="px-3 py-4 max-w-[450px]">
                        {lead.notes && lead.notes.length > 0 ? (
                          <>
                            {lead.notes?.length > 0 &&
                              (() => {
                                const latestNote = lead.notes
                                  .slice()
                                  .sort(
                                    (a, b) =>
                                      new Date(b.created_at) -
                                      new Date(a.created_at)
                                  )[0];

                                return (
                                  <div
                                    className="cursor-pointer bg-gray-50 hover:bg-gray-100 p-2 rounded border border-gray-200 transition-colors"
                                    onClick={() => handleNoteClick(lead)}
                                  >
                                    <div className="text-sm text-gray-800 max-h-[100px] overflow-y-auto pr-1">
                                      {latestNote.note}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      By: {latestNote.user_name} •{" "}
                                      {formatDistanceToNow(
                                        new Date(latestNote.created_at),
                                        {
                                          addSuffix: true,
                                        }
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                          </>
                        ) : (
                          <button
                            onClick={() => handleNoteClick(lead)}
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Add note
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {lead.branch || "N/A"}
                        </div>
                      </td>

                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center space-x-2">
                          {/* View */}
                          <div
                            className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded-md inline-flex items-center justify-center cursor-pointer transform transition-all duration-200 hover:scale-105 shadow-sm"
                            onClick={() => handleViewLeadForm(lead.id)}
                            title="View Details"
                          >
                            <IconEye size={16} className="text-white" />
                          </div>

                          {/* Create Child */}
                          <div
                            className={`p-1.5 ${
                              !lead.child_id
                                ? "bg-purple-500 hover:bg-purple-600"
                                : "bg-purple-300"
                            } rounded-md inline-flex items-center justify-center cursor-pointer transform transition-all duration-200 hover:scale-105 shadow-sm`}
                            onClick={() => {
                              if (!lead.child_id) handleCreateChildLead(lead);
                            }}
                            title="Create Child Lead"
                            style={{ opacity: lead.child_id ? 0.6 : 1 }}
                          >
                            <Baby size={16} className="text-white" />
                          </div>

                          {/* Edit */}
                          <div
                            className="p-1.5 bg-emerald-500 hover:bg-emerald-600 rounded-md inline-flex items-center justify-center cursor-pointer transform transition-all duration-200 hover:scale-105 shadow-sm"
                            onClick={() => handleEditLead(lead.id)}
                            title="Edit Lead"
                          >
                            <IconEdit size={16} className="text-white" />
                          </div>

                          {/* Delete */}
                          {role === "superadmin" && (
                            <div
                              className="p-1.5 bg-rose-600 hover:bg-rose-700 rounded-md inline-flex items-center justify-center cursor-pointer transform transition-all duration-200 hover:scale-105 shadow-sm"
                              onClick={() => handleDeleteLead(lead.id)}
                              title="Delete Lead"
                            >
                              <IconTrash size={16} className="text-white" />
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="px-6 py-10 text-center">
                      <div className="flex flex-col items-center">
                        <IconSearch className="h-12 w-12 text-gray-300 mb-3" />
                        <p className="text-gray-500 text-lg font-medium mb-1">
                          No leads found
                        </p>
                        <p className="text-gray-400 text-sm">
                          Try adjusting your filters or search criteria
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {totalLeads > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <div className="flex items-center mb-4 sm:mb-0">
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">{indexOfFirstLead + 1}</span> to{" "}
                  <span className="font-medium">
                    {Math.min(indexOfLastLead, totalLeads)}
                  </span>{" "}
                  of <span className="font-medium">{totalLeads}</span> leads
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-2 mr-4">
                  <label className="text-sm text-gray-600">Per page:</label>
                  <select
                    value={leadsPerPage}
                    onChange={(e) => handleLeadsPerPageChange(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>

                <nav
                  className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border ${
                      currentPage === 1
                        ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <span className="sr-only">First</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 border ${
                      currentPage === 1
                        ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <span className="sr-only">Previous</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border ${
                          currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 border ${
                      currentPage === totalPages
                        ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <span className="sr-only">Next</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border ${
                      currentPage === totalPages
                        ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <span className="sr-only">Last</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 6.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0zm6 0a1 1 0 010-1.414L14.586 10l-4.293-3.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reminder Modal */}
      <Modal
        isOpen={modelOpen}
        onRequestClose={() => setModelOpen(false)}
        className="modal p-0 w-full max-w-lg mx-auto"
        overlayClassName="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center p-4 z-[1000]"
      >
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex items-center">
            <IconBell className="text-amber-500 mr-3" size={24} />
            <h2 className="text-lg font-semibold text-gray-800">Reminder</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-700 leading-relaxed">{reminderMessage}</p>
          </div>
          <div className="bg-gray-50 px-6 py-3 flex justify-end">
            <button
              onClick={() => setModelOpen(false)}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Notes Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        contentLabel="Notes"
        className="modal p-0 w-full max-w-2xl mx-auto"
        overlayClassName="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center p-4 z-[1000]"
      >
        <div className="bg-white rounded-lg shadow-xl overflow-hidden flex flex-col h-[95vh] max-h-[700px]">
          <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">
                Notes for {currentLead?.parent_info?.name}
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <IconX size={20} />
              </button>
            </div>
          </div>

          {/* Notes Section - Scrollable */}
          <div className="flex-grow overflow-y-auto p-6">
            {notes.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 mx-auto text-gray-300 mb-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-500 text-lg mb-1">No notes yet</p>
                <p className="text-gray-400 text-sm">
                  Add the first note below
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {notes.map((note, index) => {
                  const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    note.user_name || "Unknown User"
                  )}&background=random&bold=true`;

                  return (
                    <div
                      key={index}
                      className={`flex ${
                        index > 0 ? "border-t border-gray-100 pt-4" : ""
                      }`}
                    >
                      <div className="flex-shrink-0 mr-4">
                        <img
                          src={avatarUrl}
                          alt={`${note.user_name || "Unknown User"}'s avatar`}
                          className="w-10 h-10 rounded-full object-cover shadow-sm border border-gray-200"
                        />
                      </div>
                      <div className="flex-grow overflow-hidden">
                        <div className="flex justify-between items-start">
                          <p className="font-medium text-gray-900">
                            {note.user_name || "Unknown User"}
                          </p>
                          <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                            {formatDateTime(note.created_at)}
                          </span>
                        </div>
                        <p className="text-gray-700 mt-1 text-sm whitespace-pre-line">
                          {note.note}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add Note Section - Fixed at bottom */}
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="flex items-start">
              <textarea
                className="flex-grow border border-gray-300 rounded-lg p-3 text-sm mr-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a new note..."
                rows={3}
              ></textarea>
              <button
                className={`px-4 py-2 rounded-lg font-medium ${
                  newNote.trim()
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                } transition-colors shadow-sm flex-shrink-0`}
                onClick={handleAddNote}
                disabled={!newNote.trim()}
              >
                Add Note
              </button>
            </div>
            <div
              className="flex items-center gap-3 my-4 bg-gray-50 p-4 rounded-md shadow-sm border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={handleDropMessageToggle}
            >
              <div className="relative">
                <input
                  type="checkbox"
                  id="drop-message"
                  className="peer sr-only"
                  checked={isDropMessageChecked}
                  onChange={handleDropMessageToggle}
                />
                <div className="w-6 h-6 border-2 border-gray-300 rounded flex items-center justify-center peer-checked:border-blue-500 peer-checked:bg-blue-500 transition-all">
                  {isDropMessageChecked && (
                    <FaCheck className="text-white text-sm" />
                  )}
                </div>
              </div>
              <label
                htmlFor="drop-message"
                className="text-sm font-medium text-gray-700 cursor-pointer"
              >
                Call not received, Drop a Message
              </label>
            </div>
          </div>
        </div>
      </Modal>

      {/* Reason for Lost Modal */}
      <Modal
        isOpen={isReasonModalOpen}
        onRequestClose={() => setIsReasonModalOpen(false)}
        contentLabel="Reason for Lost"
        className="modal p-0 w-full max-w-lg mx-auto"
        overlayClassName="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center p-4 z-[1000]"
      >
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="bg-yellow-50 px-6 py-4 border-b border-yellow-100 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-yellow-600 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h2 className="text-lg font-semibold text-gray-800">
              Reason for Marking as Lost
            </h2>
          </div>

          <div className="p-6">
            <p className="text-gray-600 mb-4">
              Please provide a reason why this lead is being marked as lost.
              This helps with tracking and improving your sales process.
            </p>

            <textarea
              className="w-full border border-gray-300 rounded-lg p-3 text-gray-700 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Chose competitor, Budget constraints, No response after follow-ups..."
              rows={4}
            ></textarea>
          </div>

          <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
            <button
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
              onClick={() => setIsReasonModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className={`px-4 py-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400 ${
                reason.trim()
                  ? "bg-yellow-500 hover:bg-yellow-600 text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
              onClick={handleSubmitReason}
              disabled={!reason.trim()}
            >
              Submit
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default LeadTable;
